/**
 * NotchesPlansPage — /n2/plans
 *
 * Public plan comparison. Five research memberships shown together;
 * The Boring Bucket is presented separately as a mentored membership.
 * Other public pages (Manual Trading, BB, About) link here for the full
 * comparison.
 *
 * Rules the copy respects:
 *   - Premier and SPX Only are the only plans wired to a live checkout.
 *     Index & ETF Research, Futures Options Research, and All Research
 *     are approved SKUs pending payment-provider configuration; their
 *     purchase affordance is an enquiry link, never a live checkout.
 *   - The Boring Bucket is shown at $299/month only. Its existing
 *     annual configuration is preserved elsewhere but not promoted here.
 *   - One active research membership per customer. Boring Bucket
 *     replaces the research membership; it does not add $299 on top.
 *   - Public market research remains free.
 *   - Coverage is fixed per plan. New markets and products are not
 *     automatically added to existing plans.
 *   - Notification availability is stated only where confirmed. The
 *     policy for combined plans and BB is being finalized separately.
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { palette, type as typeTokens } from './theme';
import {
  NotchesExplainerFrame,
  useIsMobile,
  useReducedMotion,
} from './NotchesExplainerFrame';
import {
  RESEARCH_PLANS,
  BORING_BUCKET_PLAN,
  SPECIALIST_PLAN_KEYS,
  type ResearchPlan as Plan,
} from './plansCatalog';
import PurchaseButton from './PurchaseButton';
import { isPublicPurchaseEnabled, type IntentBilling } from './purchaseIntent';

// Customer-facing billing toggle. The catalog approves both monthly
// and annual pricing for every research plan; the annual amount is
// simply hidden as a purchase option for plans whose yearlyPrice is
// null (Boring Bucket). No unapproved annual offers can surface —
// plansCatalog.ts is the source of truth for which prices exist.
type BillingCycle = IntentBilling;

const BillingToggle: React.FC<{
  value: BillingCycle;
  onChange: (v: BillingCycle) => void;
}> = ({ value, onChange }) => (
  <div
    role="tablist"
    aria-label="Billing interval"
    style={{
      display: 'inline-flex',
      border: `1px solid ${palette.ink3}`,
      borderRadius: 6,
      overflow: 'hidden',
      fontFamily: typeTokens.sans,
      fontSize: 13,
      marginBottom: 12,
      background: palette.paper,
    }}
  >
    {(['monthly', 'yearly'] as const).map((cycle) => {
      const active = cycle === value;
      const label = cycle === 'monthly' ? 'Monthly' : 'Annual';
      return (
        <button
          key={cycle}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(cycle)}
          style={{
            padding: '8px 16px',
            border: 'none',
            background: active ? palette.ink : 'transparent',
            color: active ? palette.paper : palette.ink,
            cursor: 'pointer',
            fontFamily: typeTokens.sans,
            fontSize: 13,
            fontWeight: active ? 500 : 400,
          }}
        >
          {label}
          {cycle === 'yearly' && (
            <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.75 }}>save ~17%</span>
          )}
        </button>
      );
    })}
  </div>
);

// Main bundles = every research plan that isn't a single-symbol
// specialist. Specialist plans (SPX Only, ES Only) are grouped
// separately so the lead-with-bundles narrative is preserved even as
// the standalone lineup grows.
const MAIN_RESEARCH_PLANS: Plan[] = RESEARCH_PLANS.filter(
  (p) => !SPECIALIST_PLAN_KEYS.includes(p.key),
);
const SPECIALIST_RESEARCH_PLANS: Plan[] = RESEARCH_PLANS.filter((p) =>
  SPECIALIST_PLAN_KEYS.includes(p.key),
);

export const NotchesPlansPage: React.FC = () => {
  const isMobile = useIsMobile();
  const reduced = useReducedMotion();
  const { hash } = useLocation();
  // Customer selection of billing interval — drives both the emphasized
  // price on each plan row and the billing value carried through to the
  // Stripe checkout session. Monthly is the default.
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  // Scroll the deep-linked plan row into view when the URL carries a hash
  // (e.g. /n2/plans#index-etf-research from a bundle overview CTA).
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, '');
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    }
  }, [hash, reduced]);

  return (
    <NotchesExplainerFrame
      breadcrumbTrail={[
        { label: 'Notches', to: '/n2' },
        { label: 'Plans' },
      ]}
    >
      <Hero reduced={reduced} isMobile={isMobile} />

      <Section title="Across the research plans">
        <ul style={sharedBenefitsStyle}>
          <li>
            Each researched opportunity carries supporting analysis and
            risk considerations.
          </li>
          <li>
            Public market research is free: today plus the last two days of
            daily briefings, the research library, and the public scorecard.
            The briefings archive and cross-asset analysis are Premier.
          </li>
          <li>
            Each plan's coverage is fixed. New markets and products are
            not automatically added to existing plans.
          </li>
        </ul>
      </Section>

      <Section title="Research memberships">
        <BillingToggle value={billingCycle} onChange={setBillingCycle} />
        <ul style={planListStyle}>
          {MAIN_RESEARCH_PLANS.map((plan) => (
            <PlanRow key={plan.key} plan={plan} billingCycle={billingCycle} />
          ))}
        </ul>
      </Section>

      <Section title="Specialist standalone options">
        <p style={paragraphStyle}>
          Single-market subscriptions for traders who want just one focus.
          Each specialist plan is already included in the corresponding
          bundle above; you do not need both.
        </p>
        <ul style={planListStyle}>
          {SPECIALIST_RESEARCH_PLANS.map((plan) => (
            <PlanRow key={plan.key} plan={plan} billingCycle={billingCycle} />
          ))}
        </ul>
      </Section>

      <Section title="Mentored membership">
        <p style={paragraphStyle}>
          A separate offering for members who want a guided path through
          the framework alongside the research. Monthly billing only.
        </p>
        <ul style={planListStyle}>
          <PlanRow plan={BORING_BUCKET_PLAN} billingCycle="monthly" accent />
        </ul>
      </Section>

      <Section title="Changing plans">
        <ul style={compactListStyle}>
          <li>One active research membership per customer.</li>
          <li>
            The Boring Bucket replaces your research membership.
            $299/month is the total membership price, not an additional
            charge on top of a research plan.
          </li>
          <li>
            Automated trading engines are separate subscriptions from any
            research plan.
          </li>
        </ul>
      </Section>

      <Section title="Notifications">
        <p style={paragraphStyle}>
          SMS notification of new researched opportunities is included
          with Premier, SPX Only, and ES Only.
        </p>
      </Section>

      <Section title="Where to next">
        <div style={ctaRowStyle(isMobile)}>
          <CtaLink to="/n2/manual-trading" primary>
            How Manual Trading works →
          </CtaLink>
          <CtaLink to="/n2/automated-trading">
            Automated Trading Engines →
          </CtaLink>
          <CtaLink to="/n2/boring-bucket">
            The Boring Bucket →
          </CtaLink>
          <CtaLink to="/n2">← Back to Notches</CtaLink>
        </div>
      </Section>
    </NotchesExplainerFrame>
  );
};

// ─── Building blocks ─────────────────────────────────────────────────────

const Hero: React.FC<{ reduced: boolean; isMobile: boolean }> = ({
  reduced,
  isMobile,
}) => (
  <header
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
      animation: reduced
        ? undefined
        : 'notches-explainer-fade-in 320ms ease-out both',
    }}
  >
    <span
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 12,
        fontWeight: 700,
        color: palette.ink3,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
      }}
    >
      Plans
    </span>
    <h1
      tabIndex={-1}
      style={{
        fontFamily: typeTokens.serif,
        fontSize: isMobile ? 32 : 40,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.01em',
        lineHeight: 1.15,
        textWrap: 'balance' as any,
        outline: 'none',
      }}
    >
      Choose your research coverage, or explore a mentored membership.
    </h1>
    <p
      style={{
        fontFamily: typeTokens.sans,
        fontSize: 16,
        color: palette.ink2,
        lineHeight: 1.55,
        margin: 0,
        maxWidth: 620,
      }}
    >
      Research plans covering stocks, indexes, ETFs, and futures options,
      with single-market specialist options available. The Boring Bucket
      is presented separately as a mentored membership.
    </p>
  </header>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
    <h2
      style={{
        fontFamily: typeTokens.serif,
        fontSize: 24,
        fontWeight: 400,
        margin: 0,
        color: palette.ink,
        letterSpacing: '-0.005em',
        lineHeight: 1.2,
      }}
    >
      {title}
    </h2>
    {children}
  </section>
);

const PlanRow: React.FC<{ plan: Plan; billingCycle: BillingCycle; accent?: boolean }> = ({ plan, billingCycle, accent }) => {
  const yearlyPerMonth = plan.yearlyPrice != null ? plan.yearlyPrice / 12 : null;
  // If the customer picked annual but this plan has no approved
  // annual price, the CTA falls back to monthly and we surface a
  // small note. plansCatalog is the single source of truth for
  // whether an annual price is approved.
  const effectiveBilling: BillingCycle =
    billingCycle === 'yearly' && plan.yearlyPrice == null ? 'monthly' : billingCycle;
  return (
    <li
      id={plan.key}
      style={{
        borderBottom: `1px solid ${palette.line}`,
        padding: '20px 0',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        scrollMarginTop: 96,
        ...(accent
          ? {
              borderTop: `1px solid ${palette.gold}`,
              borderBottom: `1px solid ${palette.gold}`,
              padding: '20px 20px',
              background: palette.paper2,
              borderRadius: 2,
            }
          : {}),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span
            style={{
              fontFamily: typeTokens.serif,
              fontSize: 20,
              fontWeight: 400,
              color: palette.ink,
              lineHeight: 1.2,
            }}
          >
            {plan.name}
          </span>
          <span
            style={{
              fontFamily: typeTokens.sans,
              fontSize: 14,
              color: palette.ink2,
              lineHeight: 1.45,
              maxWidth: 520,
            }}
          >
            {plan.coverage}
          </span>
        </div>
        <div
          style={{
            fontFamily: typeTokens.sans,
            fontSize: 13,
            color: palette.ink2,
            textAlign: 'right',
            minWidth: 140,
          }}
        >
          {effectiveBilling === 'yearly' && plan.yearlyPrice != null ? (
            <>
              <div style={{ color: palette.ink }}>
                <strong>${plan.yearlyPrice.toFixed(2)}</strong> / yr
              </div>
              {yearlyPerMonth != null && (
                <div style={{ color: palette.ink3, fontSize: 12 }}>
                  ${yearlyPerMonth.toFixed(2)} / mo when billed annually
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ color: palette.ink }}>
                <strong>${plan.monthlyPrice.toFixed(2)}</strong> / mo
              </div>
              {plan.yearlyPrice != null && (
                <div style={{ color: palette.ink3, fontSize: 12 }}>
                  ${plan.yearlyPrice.toFixed(2)} / yr available
                </div>
              )}
              {billingCycle === 'yearly' && plan.yearlyPrice == null && (
                <div style={{ color: palette.ink3, fontSize: 12, fontStyle: 'italic' }}>
                  Monthly only
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <p
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 14,
          color: palette.ink2,
          lineHeight: 1.55,
          margin: 0,
          maxWidth: 620,
        }}
      >
        {plan.description}
      </p>
      <ul
        style={{
          listStyle: 'disc',
          paddingLeft: 20,
          margin: 0,
          color: palette.ink2,
          fontFamily: typeTokens.sans,
          fontSize: 14,
          lineHeight: 1.55,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {plan.bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
      <PlanCta plan={plan} billing={effectiveBilling} />
    </li>
  );
};

// Purchase affordance. Only 'ready' plans point at the existing checkout
// flow. 'pending' plans point at an enquiry mailto so nobody is charged
// an incorrect amount; 'application' points at the BB apply route. A
// plan whose payment-provider configuration is not in place is never
// wired to a live checkout.
const PlanCta: React.FC<{ plan: Plan; billing: BillingCycle }> = ({ plan, billing }) => {
  if (plan.purchase === 'ready') {
    // When the operator has enabled public checkout AND the plan has
    // a live Stripe Price ID (still gated by productConfig at the
    // Lambda), render a real purchase button that preserves the
    // plan+billing intent through the auth funnel. Otherwise fall
    // back to the current "See subscription details" link into the
    // signed-in Upgrade page — no visitor is misled about whether a
    // click will initiate a charge.
    if (isPublicPurchaseEnabled()) {
      return (
        <PurchaseButton
          planKey={plan.key}
          billing={billing}
          disabledFallback={
            <Link to="/upgrade" style={{ ...inlineLinkStyle, marginTop: 2 }}>
              See subscription details →
            </Link>
          }
        />
      );
    }
    return (
      <Link to="/upgrade" style={{ ...inlineLinkStyle, marginTop: 2 }}>
        See subscription details →
      </Link>
    );
  }
  if (plan.purchase === 'application') {
    return (
      <Link to="/n2/boring-bucket" style={{ ...inlineLinkStyle, marginTop: 2 }}>
        Learn about the mentored membership →
      </Link>
    );
  }
  return (
    <span
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        marginTop: 2,
        fontFamily: typeTokens.sans,
        fontSize: 13,
        color: palette.ink3,
        lineHeight: 1.5,
        maxWidth: 320,
      }}
    >
      Not yet available for direct checkout.
    </span>
  );
};

const CtaLink: React.FC<{
  to: string;
  primary?: boolean;
  children: React.ReactNode;
}> = ({ to, primary, children }) => (
  <Link
    to={to}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 16px',
      borderRadius: 2,
      textDecoration: 'none',
      fontFamily: typeTokens.sans,
      fontSize: 14,
      fontWeight: 600,
      color: primary ? palette.paper : palette.ink,
      background: primary ? palette.goldDeep : 'transparent',
      border: `1px solid ${primary ? palette.goldDeep : palette.line}`,
      transition: 'background 150ms ease-out, color 150ms ease-out',
    }}
  >
    {children}
  </Link>
);

const paragraphStyle: React.CSSProperties = {
  fontFamily: typeTokens.sans,
  fontSize: 15,
  color: palette.ink2,
  lineHeight: 1.6,
  margin: 0,
  maxWidth: 640,
};

const inlineLinkStyle: React.CSSProperties = {
  color: palette.goldDeep,
  textDecoration: 'underline',
  textDecorationColor: palette.line,
  textUnderlineOffset: 3,
  fontWeight: 600,
};

const sharedBenefitsStyle: React.CSSProperties = {
  listStyle: 'disc',
  paddingLeft: 20,
  margin: 0,
  color: palette.ink2,
  fontFamily: typeTokens.sans,
  fontSize: 15,
  lineHeight: 1.6,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  maxWidth: 640,
};

const compactListStyle: React.CSSProperties = {
  listStyle: 'disc',
  paddingLeft: 20,
  margin: 0,
  color: palette.ink2,
  fontFamily: typeTokens.sans,
  fontSize: 15,
  lineHeight: 1.6,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  maxWidth: 640,
};

const planListStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  borderTop: `1px solid ${palette.line}`,
  display: 'flex',
  flexDirection: 'column',
};

const ctaRowStyle = (isMobile: boolean): React.CSSProperties => ({
  display: 'flex',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 12,
  flexWrap: 'wrap',
});
